<script setup lang="ts">
import { diContainer } from '@/coreContainer/container';
import { Order } from '@/coreDomainsModule/entities/order';
import { IOrderBasis } from '@/coreDomainsModule/entities/orderBasis';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';
import { DateService } from '@/coreDomainsModule/services/dateService';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { i18n } from '@/i18n';
import { computed, ref, watchEffect } from 'vue';
import OrderBasisDetailSection from './OrderBasisDetailSection.vue';

const t = i18n.t;

/**
 * コンポーネントのProps定義
 */
interface Props {
  modalState?: boolean;
  orderData?: Order;
}

const props = withDefaults(defineProps<Props>(), {
  modalState: false,
  orderData: undefined,
});

const emits = defineEmits<{ (e: 'close'): void }>();

const basisData = ref<IOrderBasis | undefined>(undefined);

const sourceOrderInfo = computed(() => basisData.value?.sourceOrderInfos.find((info) => info.sourceOrderId === props.orderData?.orderId));

/**
 * エラーモーダルを表示する
 */
const showErrorModal = async () => {
  await showInfoModal({
    modalTitle: t('エラーが発生しました。'),
    modalDescription: t('発注根拠の取得に失敗しました。'),
    isDisabledNextButton: true,
    backButtonLabel: t('OK'),
    colorStyle: 'error',
    icon: 'error',
  });
  closeModal();
};

/**
 * このモーダルを閉じる
 */
const closeModal = () => {
  emits('close');
};

watchEffect(async () => {
  // データがない場合はスキップ
  if (!props.modalState || !props.orderData) {
    basisData.value = undefined;
    return;
  }
  const orderBasisId = props.orderData.orderBasisId;
  if (!orderBasisId) {
    await showErrorModal();
    return;
  }
  // 発注根拠情報取得
  const result = await waitLoading(diContainer.getOrderBasisUseCase.execute(orderBasisId));
  if (!result || result.status.type === 'error') {
    await showErrorModal();
    return;
  }
  basisData.value = result.status.data;
});

/**
 * 単位付きの値をi18nで変換する
 * @param value - 値
 * @param unit - 単位
 * @param needConvert - 値を単位に合わせて変換する必要があるか
 */
const convertNumberWithUnitToStringWithGuard = (value?: number, unit?: string, needConvert = false): string => {
  if (!value || !unit) return '--';
  const convertedValue = needConvert ? convertLeadTimeByUnit(value, unit) : value;
  if (['month', 'months'].includes(unit)) {
    return convertedValue + t('発注根拠モーダル:月');
  } else if (['week', 'weeks'].includes(unit)) {
    return convertedValue + t('発注根拠モーダル:週');
  } else if (['day', 'days'].includes(unit)) {
    return convertedValue + t('発注根拠モーダル:日');
  }
  return convertedValue.toString();
};

/**
 * undefinedガード付きのunixTime変換関数
 * @param date - unixTimeの日付情報(undefined許容)
 */
const convertToDisplayUnixTimeWithGuard = (date?: number) =>
  date ? DateService.convertIYearMonthDayToYYYYMMDDWithSlash(DateService.convertUnixTimeToIDate(date / 1000)) : '--';
</script>
<template>
  <InfoModal
    :modal-state="modalState && !!orderData && !!basisData"
    :back-button-label="t('閉じる')"
    :modal-width="1400"
    is-disabled-next-button
    enable-outside-click
    no-description
    @on-back="closeModal"
  >
    <template #title>
      {{ t('発注根拠詳細') }}
    </template>
    <div class="order-basis-modal__container">
      <div class="base-info__container">
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('発注作成日') }}</div>
          <div class="base-info__item--value">
            {{ convertToDisplayUnixTimeWithGuard(orderData?.createdOrderDate) }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('発注数量') }}</div>
          <div class="base-info__item--value">
            {{ sourceOrderInfo?.initialOrderAmount?.addComma() ?? '--' }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('希望納期') }}</div>
          <div class="base-info__item--value">
            {{ convertToDisplayUnixTimeWithGuard(sourceOrderInfo?.initialDesiredDeliveryDate) }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('標準在庫指数(min)') }}</div>
          <div class="base-info__item--value">
            {{
              convertNumberWithUnitToStringWithGuard(
                basisData?.userAdjustedStdInventoryIndex.min,
                basisData?.userAdjustedStdInventoryIndex.option.unit
              )
            }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('標準在庫指数(max)') }}</div>
          <div class="base-info__item--value">
            {{
              convertNumberWithUnitToStringWithGuard(
                basisData?.userAdjustedStdInventoryIndex.max,
                basisData?.userAdjustedStdInventoryIndex.option.unit
              )
            }}
          </div>
        </div>
        <div class="base-info__item">
          <div class="base-info__item--label">{{ t('リードタイム') }}</div>
          <div class="base-info__item--value">
            {{ convertNumberWithUnitToStringWithGuard(basisData?.userAdjustedLeadTime.value, basisData?.userAdjustedLeadTime.unit, true) }}
          </div>
        </div>
      </div>
      <OrderBasisDetailSection :basis-data="basisData" :source-order-id="orderData?.orderId" />
    </div>
  </InfoModal>
</template>
<style lang="scss" scoped>
.order-basis-modal__container {
  display: grid;
  gap: 16px;
  border-radius: 8px;
  padding: 16px;
  background-color: $semanticPrimarySubdued;
}
.base-info__container {
  display: grid;
  grid-template-columns: repeat(6, auto);
}
.base-info__item {
  display: grid;
  justify-items: center;
  gap: 8px;
  * {
    width: fit-content;
    height: fit-content;
  }
  &--label {
    color: $textSecondary;
  }
  &--value {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    &--error {
      color: $semanticError;
    }
  }
}
</style>
